import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { DeliveryStatus } from "@/components/inbox/MessageStatusIcon";

export interface InboxMessage {
  id: string;
  client_id: string;
  target_id: string | null;
  thread_id: string;
  unipile_message_id: string | null;
  sender_name: string;
  sender_profile_url: string | null;
  sender_picture_url: string | null;
  sender_linkedin_id: string | null;
  sender_first_name: string | null;
  sender_last_name: string | null;
  sender_title: string | null;
  sender_company: string | null;
  message_text: string;
  message_preview: string | null;
  is_read: boolean;
  is_from_me: boolean;
  received_at: string;
  synced_at: string;
  created_at: string;
  updated_at: string;
  delivery_status?: DeliveryStatus;
}

export async function syncClientInbox(clientId: string, options?: { silent?: boolean }) {
  try {
    const { data, error } = await supabase.functions.invoke('sync-inbox', {
      body: { clientId }
    });

    if (error) throw error;

    if (!options?.silent) {
      toast({
        title: "Inbox Synced",
        description: `Found ${data.newMessages || 0} new messages`,
      });
    }

    return data;
  } catch (error: any) {
    console.error('Error syncing inbox:', error);
    if (!options?.silent) {
      toast({
        title: "Sync Failed",
        description: error.message || "Failed to sync inbox",
        variant: "destructive",
      });
    }
    throw error;
  }
}

export async function sendInboxReply(
  clientId: string,
  messageId: string | null,
  replyText: string,
  extra?: { targetId?: string | null; providerId?: string | null; threadId?: string | null },
) {
  const { data, error } = await supabase.functions.invoke('linkedin-send-inbox-reply', {
    body: {
      clientId,
      messageId,
      replyText,
      targetId: extra?.targetId ?? null,
      providerId: extra?.providerId ?? null,
      threadId: extra?.threadId ?? null,
    },
  });

  if (error) {
    throw new Error(error.message || 'Failed to send message');
  }

  if (!data.success) {
    if (data.errorType === 'rate_limit') {
      throw new Error(`Rate limit reached. Please wait ${Math.ceil(data.retryAfter / 1000 / 60)} minutes.`);
    }
    if (data.errorType === 'payment_required') {
      throw new Error('Automation credits exhausted. Please top up your account.');
    }
    throw new Error(data.error || 'Failed to send message');
  }

  return data;
}

export async function markMessageAsRead(messageId: string) {
  try {
    const { error } = await supabase
      .from('client_messages')
      .update({ is_read: true })
      .eq('id', messageId);

    if (error) throw error;

    return true;
  } catch (error: any) {
    console.error('Error marking message as read:', error);
    throw error;
  }
}

// Batch mark multiple messages as read in a single query
export async function markMessagesAsRead(messageIds: string[]) {
  if (messageIds.length === 0) return true;

  try {
    const { error } = await supabase
      .from('client_messages')
      .update({ is_read: true })
      .in('id', messageIds);

    if (error) throw error;

    return true;
  } catch (error: any) {
    console.error('Error marking messages as read:', error);
    throw error;
  }
}

export async function getInboxStats(clientId: string) {
  try {
    const { data: messages, error } = await supabase
      .from('client_messages')
      .select('is_read, is_from_me')
      .eq('client_id', clientId);

    if (error) throw error;

    const unreadCount = messages?.filter(m => !m.is_read && !m.is_from_me).length || 0;
    const totalMessages = messages?.length || 0;

    return { unreadCount, totalMessages };
  } catch (error: any) {
    console.error('Error getting inbox stats:', error);
    return { unreadCount: 0, totalMessages: 0 };
  }
}

// Mark all unread messages as read for a specific client
export async function markAllClientMessagesAsRead(clientId: string) {
  try {
    const { error } = await supabase
      .from('client_messages')
      .update({ is_read: true })
      .eq('client_id', clientId)
      .eq('is_read', false)
      .eq('is_from_me', false);

    if (error) throw error;

    return true;
  } catch (error: any) {
    console.error('Error marking all messages as read:', error);
    throw error;
  }
}
